"use client";

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

// ============================================================================
// Types
// ============================================================================

/** A single browser history entry (matches API response shape) */
export interface HistoryEntry {
	id: string;
	url: string;
	title: string;
	thumbnailUrl: string | null;
	visitedAt: string; // ISO date string
}

export interface WebBrowserState {
	/** Current URL being displayed */
	url: string;
	/** Display-friendly shortened URL (e.g. "www.example.com/page") */
	displayUrl: string;
	/** Site name / domain label (e.g. "Example") */
	siteName: string;
	/** Whether the iframe is currently loading */
	isLoading: boolean;
	/** Whether the browser can navigate backwards */
	canGoBack: boolean;
	/** Whether the browser can navigate forwards */
	canGoForward: boolean;
	/** Persisted browsing history entries (newest first) */
	historyEntries: HistoryEntry[];
	/** Whether history entries are still loading from API */
	historyLoading: boolean;
}

export interface WebBrowserActions {
	/** Navigate to a new URL (pushes to history) */
	navigate: (url: string) => void;
	/** Go back in history */
	goBack: () => void;
	/** Go forward in history */
	goForward: () => void;
	/** Refresh the current page */
	refresh: () => void;
	/** Close the browser (calls the provided onClose) */
	close: () => void;
	/** Copy the current URL to clipboard */
	copyLink: () => Promise<void>;
	/** Open the current URL in an external browser tab */
	openExternal: () => void;
	/** Set loading state (called by iframe on load events) */
	setIsLoading: (loading: boolean) => void;
	/** A key that increments on refresh to force iframe reload */
	refreshKey: number;
	/** Remove a single history entry by ID */
	removeHistoryEntry: (id: string) => void;
	/** Clear all history entries */
	clearHistory: () => void;
}

type WebBrowserContextValue = WebBrowserState & WebBrowserActions;

// ============================================================================
// Helpers
// ============================================================================

/** Strip protocol and trailing slash for display */
function toDisplayUrl(url: string): string {
	try {
		const u = new URL(url);
		const host = u.host.replace(/^www\./, "www.");
		const path = u.pathname === "/" ? "" : u.pathname.replace(/\/$/, "");
		return `${host}${path}`;
	} catch {
		return url;
	}
}

/**
 * Build a descriptive page title from a URL.
 * e.g. "https://facebook.com/marketplace" → "Facebook Marketplace"
 *      "https://www.example.com/blog/my-post" → "Example Blog My Post"
 */
function toPageTitle(url: string): string {
	try {
		const u = new URL(url);
		// Domain part: strip www, take name before TLD, capitalise
		const hostParts = u.hostname.replace(/^www\./, "").split(".");
		const domainName = hostParts[0] || u.hostname;
		const domain = domainName.charAt(0).toUpperCase() + domainName.slice(1);

		// Path part: split segments, clean hyphens/underscores, capitalise
		const pathSegments = u.pathname
			.split("/")
			.filter(Boolean)
			.map((seg) =>
				seg
					.replace(/[-_]/g, " ")
					.replace(/\b\w/g, (c) => c.toUpperCase()),
			);

		if (pathSegments.length === 0) return domain;
		return `${domain} ${pathSegments.join(" ")}`.trim();
	} catch {
		return "Website";
	}
}

/** Extract a human-readable site name from a URL */
function toSiteName(url: string): string {
	try {
		const u = new URL(url);
		const parts = u.hostname.replace(/^www\./, "").split(".");
		const name = parts[0] || u.hostname;
		return name.charAt(0).toUpperCase() + name.slice(1);
	} catch {
		return "Website";
	}
}

/** Ensure URL has a protocol */
function normalizeUrl(url: string): string {
	if (!url) return "";
	if (url.startsWith("http://") || url.startsWith("https://")) return url;
	return `https://${url}`;
}

// ============================================================================
// Context
// ============================================================================

const WebBrowserContext = createContext<WebBrowserContextValue | null>(null);

export interface WebBrowserProviderProps {
	children: React.ReactNode;
	/** Initial URL to load */
	initialUrl: string;
	/** Callback when the browser close button is pressed */
	onClose: () => void;
}

export function WebBrowserProvider({
	children,
	initialUrl,
	onClose,
}: WebBrowserProviderProps) {
	const normalized = normalizeUrl(initialUrl);

	// History stack and current position (in-session navigation)
	const historyRef = useRef<string[]>([normalized]);
	const indexRef = useRef(0);

	const [url, setUrl] = useState(normalized);
	const [isLoading, setIsLoading] = useState(true);
	const [canGoBack, setCanGoBack] = useState(false);
	const [canGoForward, setCanGoForward] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	// Persisted history entries from DB
	const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
	const [historyLoading, setHistoryLoading] = useState(true);
	const historyReadyRef = useRef(false);
	const pendingVisitsRef = useRef<string[]>(normalized ? [normalized] : []);

	// ── Record a visit to the DB (fire-and-forget) ────────────────────────
	const recordVisit = useCallback((visitUrl: string) => {
		const title = toPageTitle(visitUrl);
		// Generate a favicon URL from the domain
		let faviconUrl: string | null = null;
		try {
			const domain = new URL(visitUrl).hostname;
			faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
		} catch { /* invalid URL, leave null */ }

		// Generate a temporary ID (crypto.randomUUID requires secure context)
		const tempId =
			typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
				? crypto.randomUUID()
				: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
		// Optimistically prepend to local state
		const optimistic: HistoryEntry = {
			id: tempId,
			url: visitUrl,
			title,
			thumbnailUrl: faviconUrl,
			visitedAt: new Date().toISOString(),
		};
		setHistoryEntries((prev) => [optimistic, ...prev]);

		// Persist to DB
		fetch("/api/browser-history", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ url: visitUrl, title, thumbnailUrl: faviconUrl }),
		})
			.then(async (res) => {
				if (res.ok) {
					const data = await res.json();
					// Replace optimistic entry with real one
					if (data.entry) {
						setHistoryEntries((prev) =>
							prev.map((e) => (e.id === optimistic.id ? data.entry : e)),
						);
					}
				}
			})
			.catch((err) => console.warn("[WebBrowser] Failed to record visit:", err));
	}, []);

	// ── Fetch history before recording this session's visits ─────────────
	useEffect(() => {
		let cancelled = false;
		async function fetchHistory() {
			try {
				const res = await fetch("/api/browser-history");
				if (res.ok) {
					const data = await res.json();
					if (!cancelled) setHistoryEntries(data.entries ?? []);
				}
			} catch (err) {
				console.warn("[WebBrowser] Failed to fetch history:", err);
			} finally {
				if (!cancelled) {
					historyReadyRef.current = true;
					setHistoryLoading(false);
					const pendingVisits = pendingVisitsRef.current.splice(0);
					pendingVisits.forEach(recordVisit);
				}
			}
		}
		fetchHistory();
		return () => { cancelled = true; };
	}, [recordVisit]);

	const recordOrQueueVisit = useCallback((visitUrl: string) => {
		if (!historyReadyRef.current) {
			pendingVisitsRef.current.push(visitUrl);
			return;
		}
		recordVisit(visitUrl);
	}, [recordVisit]);

	const updateNavState = useCallback(() => {
		setCanGoBack(indexRef.current > 0);
		setCanGoForward(indexRef.current < historyRef.current.length - 1);
	}, []);

	const navigate = useCallback(
		(newUrl: string) => {
			const norm = normalizeUrl(newUrl);
			historyRef.current = historyRef.current.slice(0, indexRef.current + 1);
			historyRef.current.push(norm);
			indexRef.current = historyRef.current.length - 1;
			setUrl(norm);
			setIsLoading(true);
			updateNavState();
			recordOrQueueVisit(norm);
		},
		[recordOrQueueVisit, updateNavState],
	);

	const goBack = useCallback(() => {
		if (indexRef.current > 0) {
			indexRef.current -= 1;
			setUrl(historyRef.current[indexRef.current]);
			setIsLoading(true);
			updateNavState();
		}
	}, [updateNavState]);

	const goForward = useCallback(() => {
		if (indexRef.current < historyRef.current.length - 1) {
			indexRef.current += 1;
			setUrl(historyRef.current[indexRef.current]);
			setIsLoading(true);
			updateNavState();
		}
	}, [updateNavState]);

	const refresh = useCallback(() => {
		setIsLoading(true);
		setRefreshKey((k) => k + 1);
	}, []);

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const copyLink = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(url);
		} catch {
			// Fallback for non-secure contexts (HTTP)
			try {
				const textarea = document.createElement("textarea");
				textarea.value = url;
				textarea.style.position = "fixed";
				textarea.style.opacity = "0";
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand("copy");
				document.body.removeChild(textarea);
			} catch {
				console.warn("[WebBrowser] copy fallback failed");
			}
		}
	}, [url]);

	const openExternal = useCallback(() => {
		window.open(url, "_blank", "noopener,noreferrer");
	}, [url]);

	// ── Remove a single history entry ─────────────────────────────────────
	const removeHistoryEntry = useCallback((id: string) => {
		setHistoryEntries((prev) => prev.filter((e) => e.id !== id));
		fetch(`/api/browser-history?id=${encodeURIComponent(id)}`, {
			method: "DELETE",
		}).catch((err) => console.warn("[WebBrowser] Failed to delete entry:", err));
	}, []);

	// ── Clear all history ─────────────────────────────────────────────────
	const clearHistory = useCallback(() => {
		setHistoryEntries([]);
		fetch("/api/browser-history", { method: "DELETE" }).catch((err) =>
			console.warn("[WebBrowser] Failed to clear history:", err),
		);
	}, []);

	const displayUrl = useMemo(() => toDisplayUrl(url), [url]);
	const siteName = useMemo(() => toSiteName(url), [url]);

	const value = useMemo<WebBrowserContextValue>(
		() => ({
			url,
			displayUrl,
			siteName,
			isLoading,
			canGoBack,
			canGoForward,
			historyEntries,
			historyLoading,
			navigate,
			goBack,
			goForward,
			refresh,
			close,
			copyLink,
			openExternal,
			setIsLoading,
			refreshKey,
			removeHistoryEntry,
			clearHistory,
		}),
		[
			url,
			displayUrl,
			siteName,
			isLoading,
			canGoBack,
			canGoForward,
			historyEntries,
			historyLoading,
			navigate,
			goBack,
			goForward,
			refresh,
			close,
			copyLink,
			openExternal,
			refreshKey,
			removeHistoryEntry,
			clearHistory,
		],
	);

	return (
		<WebBrowserContext.Provider value={value}>
			{children}
		</WebBrowserContext.Provider>
	);
}

/** Hook to access web browser state and actions */
export function useWebBrowser(): WebBrowserContextValue {
	const ctx = useContext(WebBrowserContext);
	if (!ctx) {
		throw new Error("useWebBrowser must be used within a WebBrowserProvider");
	}
	return ctx;
}
